import {ReactElement} from 'react';
import classnames from 'classnames';
import Heading, {HEADING_TYPES} from '@frontend/ui-kit/Components/Heading';
import Text from '@frontend/ui-kit/Components/Text';
import Slider, {SwiperSlide} from '@frontend/ui-kit/Components/Slider';
import Feedback from '../Feedback';
import appStore from '../../../static/images/appStore.svg';
import googlePlay from '../../../static/images/googlePlay.svg';
import {gettext} from '../../../translationService';
import './index.scss';

const FEEDBACKS = [
    {
        title: gettext('I love this app'),
        rating: 5,
        text: gettext('This app has brought so much convenience to my life while saving me time and stress. I’m so grateful that my employer provides it. The service saves money on medical stuff because there are many instances when you can use their telemedicine service instead of going to a doctor in person. It has saved me a lot of money. They’ve also helped me find new in-network providers that can take new patients on short notice. This kind of thing saves me hours of my life that I would’ve spent on the phone. There’s so much frustration dealing with doctors and health insurance; HealthJoy makes it all easier.'),
        date: '09/18/2021',
        logo: appStore
    },
    {
        title: gettext('Great app'),
        rating: 5,
        text: gettext('I needed this app a long time ago. I got prescriptions refilled when my doctor went on vacation, medication for a sinus infection. No one wants to wait a day to get an appointment and then drive to wait an hour to see a doctor for them to tell you what you already know. Then drive to the pharmacy to wait on them to give you the medication. Healthjoy had a physician assistant call me, talk through my symptoms, and call in a prescription to my pharmacy—all while I was at home! It took 1 hour total from when I logged into the app until my pharmacy texted me they received my prescription! I only want to use HealthJoy from now on.'),
        date: '07/13/2021',
        logo: appStore
    },
    {
        title: gettext('Makes my life so much easier!'),
        rating: 5,
        text: gettext('Using HealthJoy is truly a joy! It is an incredible perk I get with my job. As a new manager/ leader in my company, I have found it challenging to find time to make multiple phone calls to insurance and to doctor\'s offices to make appointments. With HealthJoy, by communicating with one of their concierges, it only takes a few "clicks" and selections to find a provider and get scheduled with them. It also shows your payments/ copays in writing so there is much less guesswork and worry about surprise payments. I feel like I could never function without HealthJoy again!'),
        date: '03/08/2023',
        logo: appStore
    },
    {
        title: gettext('Eliminates confusion!'),
        rating: 5,
        text: gettext('This app is so helpful! It eliminates the confusion caused by our convoluted insurance system by laying everything out based on your needs and preferences. It helps me find new providers and schedule appointments, which is my favorite feature. I\'m honestly amazed and grateful they\'ve made healthcare so easy. Thanks, HealthJoy.'),
        date: '12/22/22',
        logo: googlePlay
    },
    {
        title: gettext('Superior customer service'),
        rating: 5,
        text: gettext('Really excellent app for navigating the healthcare system, I can\'t recommend it highly enough. Every request I made was filled promptly, thoroughly, and thoughtfully. Some of the best customer service I\'ve ever experienced, hands down.'),
        date: '4/8/23',
        logo: googlePlay
    },
    {
        title: gettext('Sets a new standard for care'),
        rating: 5,
        text: gettext('The app is excellent, but that\'s not why I give the top rating. The service that HeathJoy provides is top-notch. Since the pandemic, I think we have given up on finding good service or lowered our acceptable standards. I am new to the service (four months), and I am happy to report that I have had a wonderful experience. I live in a rural area with limited services, and HeathJoy has been a savior.'),
        date: '3/18/23',
        logo: googlePlay
    }
];

type Props = {
    className?: string;
}

const Feedbacks = ({className}: Props): ReactElement => {
    const getFeedback = (feedback, index) => {
        const feedbackProps = {
            ...feedback,
            title: gettext(feedback.title),
            text: gettext(feedback.text)
        };

        return <SwiperSlide key={index}><Feedback {...feedbackProps}/></SwiperSlide>;
    };

    const sliderProps = {
        className: 'product-info-feedbacks-slider',
        slides: FEEDBACKS.map(getFeedback),
        slidesPerView: 1,
        navigation: true,
        pagination: true,
        spaceBetween: 0,
        breakpoints: {
            768: {
                slidesPerView: 3,
                spaceBetween: 8
            }
        }
    };

    return (
        <div className={classnames('product-info-feedbacks', className)}>
            <Heading type={HEADING_TYPES['4']} className='product-info-feedbacks__title'>
                {gettext('What do others have to say about HealthJoy?')}
            </Heading>
            <Text className='product-info-feedbacks__text'>
                {gettext('Hear from real-life HealthJoy users about their personal experiences')}:
            </Text>

            <Slider {...sliderProps}/>
        </div>
    );
};

export {Feedbacks as TestableFeedbacks};
export default Feedbacks;
